/* eslint-disable react/prop-types */
import { useLayoutEffect, useRef, useState } from "react";
import { HORIZON_DAYS, fmtDM, isoInDays } from "../../../lib/aircraftTimeline";

/**
 * Linha do tempo dos próximos 90 dias: "hoje" à esquerda, uma faixa por tipo de prazo
 * (documentos e seguro; manutenção por calendário; manutenção por horas, com data estimada).
 *
 *   ● círculo cheio = data certa         ○ tracejado = data estimada pelos voos das últimas 4 semanas
 *   ◆ losango      = já passou o prazo (dentro da tolerância ou da extensão do motor), preso ao "hoje"
 *   barra âmbar    = até quando dura a tolerância / a extensão
 * As cores seguem a lista de avisos: vermelho (bloqueia), âmbar (atenção), azul (em breve), cinzento (mais longe).
 */

const LANES = [
  { key: "docs", label: "Documentos e seguro" },
  { key: "cal", label: "Manutenção, por calendário" },
  { key: "hours", label: "Manutenção, por horas (data estimada)" },
];

const COLOR = {
  critical: { bg: "bg-red-500", border: "border-red-500", text: "text-red-300" },
  warn: { bg: "bg-amber-400", border: "border-amber-400", text: "text-amber-200" },
  info: { bg: "bg-blue-400", border: "border-blue-400", text: "text-blue-200" },
  ok: { bg: "bg-slate-500", border: "border-slate-500", text: "text-slate-300" },
};

const PAD = 12;
const ROW_H = 15;
const BASE_Y = 9;
const LABEL_TOP = 21;
const TICKS = [0, 15, 30, 45, 60, 75, 90];

export function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setW(el.clientWidth);
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function labelOf(e) {
  let when = "";
  if (e.overdue) when = e.barKind === "extension" ? "em extensão" : e.severity === "critical" ? "vencido" : "em tolerância";
  else if (e.date) when = `${e.estimated ? "~" : ""}${fmtDM(e.date)}`;
  return when ? `${e.title} · ${when}` : e.title;
}

/** Põe cada etiqueta numa linha própria quando se sobrepõem (do mais perto do "hoje" para o mais longe). */
function layoutLane(list, width) {
  const usable = Math.max(160, width - 2 * PAD);
  const rows = [];
  const placed = list.map((e) => {
    const x = PAD + (e.days / HORIZON_DAYS) * usable;
    const text = labelOf(e);
    const w = text.length * 6.1 + 6;
    let left = x + 10;
    if (left + w > width - PAD) left = Math.max(PAD, x - 10 - w);   // perto da borda direita: etiqueta para a esquerda
    return { e, x, text, left, w, barW: e.barDays != null ? (Math.min(e.barDays, HORIZON_DAYS) / HORIZON_DAYS) * usable : 0 };
  });
  placed.forEach((p) => {
    let r = 0;
    while ((rows[r] || []).some(([a, b]) => p.left < b + 6 && p.left + p.w > a - 6)) r += 1;
    (rows[r] = rows[r] || []).push([p.left, p.left + p.w]);
    p.row = r;
  });
  return { placed, rowCount: Math.max(1, rows.length), usable };
}

function Marker({ p, onGo }) {
  const c = COLOR[p.e.severity] || COLOR.ok;
  const shape = p.e.overdue
    ? `w-[10px] h-[10px] rotate-45 rounded-[2px] ${c.bg}`
    : p.e.estimated
      ? `w-[11px] h-[11px] rounded-full border-2 border-dashed bg-slate-900 ${c.border}`
      : `w-[11px] h-[11px] rounded-full ${c.bg}`;
  return (
    <button
      type="button"
      onClick={() => onGo?.(p.e.target)}
      title={p.e.detail}
      aria-label={p.e.detail}
      className="absolute -translate-x-1/2 -translate-y-1/2 p-1 group"
      style={{ left: p.x, top: BASE_Y }}
      data-testid={`di-tl-${p.e.id}`}
    >
      <span className={`block ${shape} ring-2 ring-slate-900 group-hover:ring-4 transition-all`} />
    </button>
  );
}

function Lane({ lane, list, width, onGo }) {
  const { placed, rowCount, usable } = layoutLane(list, width);
  const height = LABEL_TOP + rowCount * ROW_H + 2;
  return (
    <div className="mt-2" data-testid={`di-tl-lane-${lane.key}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">{lane.label}</div>
      <div className="relative" style={{ height }}>
        {TICKS.map((d) => (
          <div key={d} className={`absolute top-0 bottom-0 border-l ${d === 0 ? "border-slate-500" : "border-slate-800"}`} style={{ left: PAD + (d / HORIZON_DAYS) * usable }} />
        ))}
        {placed.map((p) => p.barW > 0 && (
          <div key={`bar-${p.e.id}`} className="absolute rounded-full bg-amber-400/35" style={{ left: PAD, width: p.barW, top: BASE_Y - 2, height: 4 }} title={p.e.detail} />
        ))}
        {placed.map((p) => (
          <div key={`lead-${p.e.id}`} className="absolute bg-slate-600/70" style={{ left: p.x - 0.5, width: 1, top: BASE_Y, height: LABEL_TOP + p.row * ROW_H + 6 - BASE_Y }} />
        ))}
        {placed.map((p) => (
          <div
            key={`lab-${p.e.id}`}
            className={`absolute text-[10.5px] leading-[14px] whitespace-nowrap ${(COLOR[p.e.severity] || COLOR.ok).text}`}
            style={{ left: p.left, top: LABEL_TOP + p.row * ROW_H }}
          >
            {p.text}
          </div>
        ))}
        {placed.map((p) => <Marker key={p.e.id} p={p} onGo={onGo} />)}
      </div>
    </div>
  );
}

function LegendDot({ cls, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block w-2 h-2 rounded-full ${cls}`} />{label}
    </span>
  );
}

export default function MaintenanceTimeline({ timeline, onGo }) {
  const [ref, measured] = useWidth();
  const width = measured || 560;
  const { entries, beyond, noEstimate, perDayMin } = timeline;
  const lanes = LANES.filter((l) => entries.some((e) => e.lane === l.key));
  const usable = Math.max(160, width - 2 * PAD);

  return (
    <div ref={ref} className="w-full" data-testid="di-timeline">
      {entries.length === 0 ? (
        <div className="text-[11px] text-slate-500 italic">Sem marcos nos próximos {HORIZON_DAYS} dias.</div>
      ) : (
        <>
          <div className="relative h-4 text-[10px] text-slate-500 select-none" aria-hidden="true">
            {TICKS.map((d) => (
              <span
                key={d}
                className={`absolute -translate-x-1/2 whitespace-nowrap ${d === 0 ? "text-slate-300 font-semibold" : ""}`}
                style={{ left: PAD + (d / HORIZON_DAYS) * usable }}
              >
                {d === 0 ? "Hoje" : fmtDM(isoInDays(d))}
              </span>
            ))}
          </div>
          {lanes.map((l) => <Lane key={l.key} lane={l} list={entries.filter((e) => e.lane === l.key)} width={width} onGo={onGo} />)}
        </>
      )}
      <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500" data-testid="di-timeline-legend">
        <LegendDot cls="bg-red-500" label="vencido" />
        <LegendDot cls="bg-amber-400" label="atenção" />
        <LegendDot cls="bg-blue-400" label="em breve" />
        <LegendDot cls="bg-slate-500" label="mais longe" />
        <span>◆ já passou o prazo</span>
        <span>○ data estimada{perDayMin > 0 ? ` (voo recente: ${((perDayMin * 7) / 60).toFixed(1)} h por semana)` : ""}</span>
        <span><span className="inline-block w-4 h-1 rounded-full bg-amber-400/40 align-middle mr-1" />tolerância ou extensão</span>
      </div>
      {(beyond > 0 || noEstimate > 0) && (
        <div className="mt-1 text-[10px] text-slate-500">
          {beyond > 0 && <span>{beyond} {beyond === 1 ? "prazo fica" : "prazos ficam"} para depois dos {HORIZON_DAYS} dias. </span>}
          {noEstimate > 0 && <span>Sem voos nas últimas 4 semanas: não dá para estimar a data de {noEstimate} {noEstimate === 1 ? "tarefa por horas" : "tarefas por horas"}.</span>}
        </div>
      )}
    </div>
  );
}
